import React from "react"
import {
	MediaContent,
	Flex,
	Card,
	TagStyled,
	Text,
	DynamicLink,
	Image,
} from "@capioweb/ui-components"
import { StringProperty } from "../../@types/EpiFoundationSpa/Property"
import { BlockCommonLayout } from "../../utils/layout"
import {
	imageUrlToImageProps,
	sendCampaignBlockClickDataLayerPush,
} from "../../utils"

interface CampaignProps {
	title: string
	preamble: string
	imageUrl: string
	imageAlternateText: string
	label: string
	pageUrl: string
}

interface CapioNexusCampaignContainerBlockProps {
	title: StringProperty
	preamble: StringProperty
	campaigns: CampaignProps[]
}

const Campaign: React.FC<CampaignProps> = (props) => {
	return (
		<Card
			w="100%"
			h={48.5}
			media={
				<img
					{...imageUrlToImageProps(
						props.imageUrl,
						"",
						props.imageAlternateText,
						true,
						true,
						[{ width: 600, breakpoint: 2560 }]
					)}
				/>
			}
			tag={props.label}
			borderColor="blue33"
		>
			<Card.Title>{props.title}</Card.Title>
			<Card.Preamble>{props.preamble}</Card.Preamble>
		</Card>
	)
}

const CapioNexusCampaignContainerBlock: React.FC<CapioNexusCampaignContainerBlockProps> =
	({ preamble, campaigns, ...rest }) => {
		return (
			campaigns?.length > 0 && (
				<BlockCommonLayout {...rest} description={preamble}>
					{campaigns && campaigns.length > 1 ? (
						<Flex
							w={{ _: 1, m: "calc(100% + 24px)" }}
							flexDirection={{ _: "column", m: "row" }}
							flexWrap="wrap"
						>
							{campaigns.map((c, i) => (
								<Flex
									key={`campaign${i}`}
									w={1}
									flexShrink={0}
									flexBasis={
										campaigns.length === 2
											? {
													_: 1,
													m: "50%",
													l: i === 0 ? "calc(100% / 3 * 2)" : "calc(100% / 3 )",
											  }
											: campaigns.length === 3
											? {
													_: 1,
													m: "50%",
													l: "calc(100% / 3)",
													xl: i === 0 ? "50%" : "25%",
											  }
											: {
													_: 1,
													m: "50%",
													xl: "25%",
											  }
									}
									px={{ _: 0, m: 1.5 }}
									mb={{ _: 2, m: 3 }}
								>
									{c?.pageUrl && (
										<DynamicLink
											url={c.pageUrl ?? "#"}
											onClick={() =>
												sendCampaignBlockClickDataLayerPush(c.title)
											}
										>
											<Campaign {...c} />
										</DynamicLink>
									)}
								</Flex>
							))}
						</Flex>
					) : (
						campaigns.map((c, i) => (
							<DynamicLink
								key={`MediaContent${i}`}
								url={c?.pageUrl ?? "#"}
								onClick={() => sendCampaignBlockClickDataLayerPush(c.title)}
							>
								{/* Renders MediaContent on screen size m+ */}
								<Flex display={{ _: "none", m: "block" }}>
									<MediaContent
										media={
											<img
												{...imageUrlToImageProps(
													c.imageUrl,
													"",
													c.imageAlternateText,
													true,
													true,
													[{ width: 300, breakpoint: 2560 }]
												)}
											/>
										}
									>
										<Flex flexDirection="column" h={1} alignItems="center">
											<Flex
												alignContent="center"
												w={1}
												flexDirection="column"
												m="auto"
											>
												{c.label && (
													<Flex mb={2}>
														<TagStyled>{c.label}</TagStyled>
													</Flex>
												)}
												<MediaContent.Title>{c.title}</MediaContent.Title>
												<Text mb={0}>{c.preamble}</Text>
											</Flex>
										</Flex>
									</MediaContent>
								</Flex>
								{/* Renders campaign card on screen size s */}
								<Flex display={{ _: "block", m: "none" }}>
									<Campaign {...c} />
								</Flex>
							</DynamicLink>
						))
					)}
				</BlockCommonLayout>
			)
		)
	}

export { CapioNexusCampaignContainerBlock }
